import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { MapPin, Loader2 } from "lucide-react";
import { useGoogleMaps } from "@/hooks/useGoogleMaps";
import { VenueSearch } from "./VenueForm/VenueSearch";
import { AddressSection } from "./VenueForm/AddressSection";
import { ContactSection } from "./VenueForm/ContactSection";
import { CoordinatesSection } from "./VenueForm/CoordinatesSection";
import { GoogleMapsError } from "./VenueForm/GoogleMapsError";
import { venueFormSchema, VenueFormValues, VenueFormProps } from "./VenueForm/types";
import { toast } from "sonner";

export function VenueForm({ onSubmit, onCancel, initialValues = {} }: VenueFormProps) {
  const [isLoadingPlaces, setIsLoadingPlaces] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isLoaded: isGoogleMapsLoaded, error: googleMapsError } = useGoogleMaps();
  
  const form = useForm<VenueFormValues>({
    resolver: zodResolver(venueFormSchema),
    defaultValues: {
      name: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      phone: "",
      website: "",
      hours: "",
      latitude: undefined,
      longitude: undefined,
      maps_link: "",
      ...initialValues,
    },
  });

  const handleFormSubmit = async (values: VenueFormValues) => {
    try {
      setIsSubmitting(true);
      console.log("Submitting venue form with values:", values);
      
      // Ensure required fields are present
      if (!values.name || !values.address) {
        throw new Error("Name and address are required");
      }
      
      // Convert empty strings to null for optional fields and ensure numbers are numbers
      const venueData = {
        name: values.name.trim(),
        address: values.address.trim(),
        city: values.city?.trim() || null,
        state: values.state?.trim() || null,
        zip: values.zip?.trim() || null,
        phone: values.phone?.trim() || null,
        website: values.website?.trim() || null,
        hours: values.hours?.trim() || null,
        latitude: values.latitude ? Number(values.latitude) : null,
        longitude: values.longitude ? Number(values.longitude) : null,
        maps_link: values.maps_link?.trim() || null,
      };
      
      console.log("Processed venue data:", venueData);
      await onSubmit(venueData);
    } catch (error) {
      console.error("Error submitting venue form:", error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("Failed to submit venue form");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Conditionally render error state
  if (googleMapsError) {
    return <GoogleMapsError errorMessage={googleMapsError} onClose={onCancel} />;
  }

  return (
    <Form {...form}>
      <form 
        onSubmit={form.handleSubmit(handleFormSubmit)} 
        className="space-y-4"
        noValidate
      >
        <VenueSearch 
          form={form} 
          isGoogleMapsLoaded={isGoogleMapsLoaded} 
          isLoadingPlaces={isLoadingPlaces} 
          setIsLoadingPlaces={setIsLoadingPlaces} 
        />

        <AddressSection form={form} />
        
        <ContactSection form={form} />
        
        <CoordinatesSection form={form} />

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" className="flex items-center gap-1" disabled={isSubmitting}>
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MapPin className="h-4 w-4" />
            )}
            {isSubmitting ? "Adding..." : "Add Venue"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
